import { columnIsHeader, rowIsHeader, TableMap } from "@tiptap/pm/tables";
import { findParentNodeClosestToPos, useEditorState } from "@tiptap/react";
import { isEqual, sortBy } from "es-toolkit";

import {
  getCellVerticalAlignment,
  type CellVerticalAlignment,
} from "@/components/tiptap/tables/TableCellVerticalAlignment";
import { getCellPosition } from "@/components/tiptap/tables/utils";
import { isCellSelection } from "@/components/tiptap/utils/isCellSelection";

import type { CellAttrs, RowAttrs, TableAttrs } from "@/components/tiptap/tables/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { Color } from "@corca/data";
import type { NodeWithPos } from "@tiptap/react";

type TableProps = Partial<{
  tableNode: NodeWithPos;
  headerRowIsEnabled: boolean;
  headerColumnIsEnabled: boolean;
  showHorizontalLines: boolean;
  showVerticalLines: boolean;
}>;

export function useTableEditorState(doc: DocumentModel) {
  return useEditorState({
    editor: doc.editor.tiptap || null,
    selector: (): TableProps => {
      if (!doc.editor.tiptap) {
        return {};
      }

      const tableNode = findParentNodeClosestToPos(
        doc.editor.tiptap.state.selection.$from,
        (n) => n.type.name === "table",
      );

      if (!tableNode) {
        return {};
      }

      const attrs = tableNode.node.attrs as TableAttrs;
      const tableMap = TableMap.get(tableNode.node);

      return {
        tableNode,
        headerRowIsEnabled: rowIsHeader(tableMap, tableNode.node, 0),
        headerColumnIsEnabled: columnIsHeader(tableMap, tableNode.node, 0),
        showHorizontalLines: attrs.showHorizontalLines,
        showVerticalLines: attrs.showVerticalLines,
      };
    },
    equalityFn: isEqual,
  });
}

type TableCellProps = Partial<{
  tableCellNode: NodeWithPos;
  tableHeaderNode: NodeWithPos;
  address: string;
  backgroundColor: Color | null;
  verticalAlignment: CellVerticalAlignment | null;
  colWidth: number | null;
  rowHeight: number | null;
}>;

export function useTableCellEditorState(doc: DocumentModel) {
  return useEditorState({
    editor: doc.editor.tiptap || null,
    selector: (): TableCellProps => {
      const { tiptap } = doc.editor;

      if (!tiptap) {
        return {};
      }

      const tableCellNode = findParentNodeClosestToPos(
        tiptap.state.selection.$from,
        (n) => n.type.name === "tableCell",
      );
      const tableHeaderNode = findParentNodeClosestToPos(
        tiptap.state.selection.$from,
        (n) => n.type.name === "tableHeader",
      );
      const tableCell = tableCellNode || tableHeaderNode;

      if (!tableCell) {
        return {};
      }

      const tableRowNode = findParentNodeClosestToPos(
        tiptap.state.selection.$from,
        (n) => n.type.name === "tableRow",
      );

      const { height } = (tableRowNode?.node.attrs ?? {}) as RowAttrs;
      const { colwidth, backgroundColor } = tableCell.node.attrs as CellAttrs;
      const verticalAlignment = getCellVerticalAlignment(tiptap);

      let address = getCellPosition(tiptap.state.doc, tableCell.pos)?.address ?? "";

      if (isCellSelection(tiptap.state.selection)) {
        const ranges = [...tiptap.state.selection.ranges];
        const limitedRanges =
          ranges.length > 6 ? [...ranges.slice(0, 5), ranges[ranges.length - 1]] : ranges;

        const selectionAddress = sortBy(limitedRanges, [(r) => r.$from.pos])
          .map(({ $from }) => getCellPosition(tiptap.state.doc, $from.pos - 1)?.address ?? "")
          .filter(Boolean);

        if (ranges.length > 6) {
          selectionAddress[4] = "...";
        }

        address = selectionAddress.join(", ");
      }

      return {
        address,
        tableCellNode,
        tableHeaderNode,
        backgroundColor,
        verticalAlignment,
        colWidth: colwidth?.[0] ?? null,
        rowHeight: height ?? null,
      };
    },
    equalityFn: isEqual,
  });
}
